import { useTranslation } from "react-i18next";
import { DATE_OPTIONS } from "../../../core/utils/date-utils";
import { BILLING_STATUS_LIST, billingStatusKey } from "../../../enums/ReportBillingStatusEnum";
import {
    QuickChip,
    QuickDateRange,
    QuickModalityFilter,
    QuickResetLink,
    QuickSearch,
    useAppliedFilter,
    useQuickSearch,
} from "../../quick-filter/QuickFilterParts";
import { ExtraFiltersButton, ExtraFilterTags, isFieldActive } from "../../quick-filter/ExtraFilters";
import {
    BILLING_DATE_TYPE,
    buildDefaultReportBillingFilter,
    fromFilterDate,
    toFilterDates,
} from "./report-billing-filter-defaults";
import { REPORT_BILLING_EXTRA_FIELDS } from "./report-billing-extra-filters";

// Son 15 gün sayfanın varsayılanı (bkz. report-billing-filter-defaults).
const DATE_PRESETS = [
    { key: 'today', label: 'Today', option: DATE_OPTIONS.TODAY },
    { key: 'yesterday', label: 'Yesterday', option: DATE_OPTIONS.YESTERDAY },
    { key: 'last15', label: 'Last 15D', option: DATE_OPTIONS.LAST_15_DAYS },
    { key: 'last30', label: 'Last 30D', option: DATE_OPTIONS.LAST_30_DAYS },
];

// Sol paneldeki tarih türleri, aynı etiketlerle.
const DATE_TYPES = [
    { value: BILLING_DATE_TYPE.CONFIRM, label: 'Confirm Date Filter' },
    { value: BILLING_DATE_TYPE.REPORT, label: 'Report Date' },
    { value: BILLING_DATE_TYPE.STUDY, label: 'Study Date' },
];

/**
 * Faturalandırma grid'inin üstündeki hızlı filtre çubuğu — rapor/çekim grid'indekiyle aynı düzen ve parçalar
 * (quick-filter/): arama, tarih türü + tarih, faturalandırma durumu, modalite, diğer filtreler. Sol panelde
 * (ReportBillingFilter) olup çubukta düğmesi olmayan her şey "Diğer filtreler"de; uzun vadede sol panelin yerini alır.
 */
export default function ReportBillingQuickFilterBar() {
    const { t } = useTranslation();
    const { filter, apply, replace, ready } = useAppliedFilter('reportBillingFilter');
    const search = useQuickSearch(filter, apply);

    if (!ready)
        return null;

    // Rapor çubuğundan farkı: tarih türü tek alanda (dateType) ve dönemi o belirliyor (varsayılan onay tarihi) —
    // görünmezse kullanıcı listenin hangi tarihe göre süzüldüğünü bilemez, bu yüzden çubukta seçilebilir.
    const dateType = filter.dateType ?? BILLING_DATE_TYPE.CONFIRM;

    // Filtrede tarihler sunucuya gidecek string olarak durur ve biçimi türe bağlı; tarih bloğu moment ister.
    // Gösterirken çözülür, uygularken (tür değişiminde de) türün biçimine yeniden yazılır.
    const dates = {
        dateBegin: fromFilterDate(dateType, filter.dateBegin),
        dateEnd: fromFilterDate(dateType, filter.dateEnd),
    };
    const applyDates = (changes) => apply(toFilterDates(dateType, changes.dateBegin, changes.dateEnd));
    const changeDateType = (next) => apply({
        dateType: next,
        ...toFilterDates(next, dates.dateBegin, dates.dateEnd),
    });

    // Çoklu seçim, sol paneldeki gibi; hiçbiri seçili değilse tüm durumlar.
    const statuses = filter.billingStatusList || [];
    const toggleStatus = (status) => apply({
        billingStatusList: statuses.includes(status)
            ? statuses.filter(s => s !== status)
            : [...statuses, status],
    });

    const extraCount = REPORT_BILLING_EXTRA_FIELDS.filter(field => isFieldActive(field, filter)).length;

    return (
        <div className="qf">
            <QuickSearch
                search={search}
                hint={t('Search by patient name, ID, accession number or description - searches all dates')}
            />

            <div className="qf-row">
                {/* Arama tüm tarihlerde çalışır (sunucu tarihi yok sayar) → tür de tarihlerle birlikte kilitlenir. */}
                <div
                    className={`qf-group${search.active ? ' is-disabled' : ''}`}
                    role="group"
                    aria-label={t('Date Type')}
                >
                    <div className="qf-seg">
                        {DATE_TYPES.map(type => (
                            <button
                                key={type.value}
                                type="button"
                                disabled={search.active}
                                aria-pressed={dateType === type.value}
                                onClick={() => dateType !== type.value && changeDateType(type.value)}
                            >
                                {t(type.label)}
                            </button>
                        ))}
                    </div>
                </div>

                <QuickDateRange
                    filter={dates}
                    apply={applyDates}
                    beginKey="dateBegin"
                    endKey="dateEnd"
                    presets={DATE_PRESETS}
                    disabled={search.active}
                    label={t(DATE_TYPES.find(type => type.value === dateType)?.label)}
                />

                <div className="qf-group" role="group" aria-label={t('Billing Status')}>
                    <div className="qf-seg">
                        <button
                            type="button"
                            aria-pressed={statuses.length === 0}
                            onClick={() => apply({ billingStatusList: [] })}
                        >
                            {t('All')}
                        </button>
                    </div>
                    {BILLING_STATUS_LIST.map(status => (
                        <QuickChip
                            key={status}
                            pressed={statuses.includes(status)}
                            onClick={() => toggleStatus(status)}
                        >
                            {t(billingStatusKey(status))}
                        </QuickChip>
                    ))}
                </div>

                <QuickModalityFilter filter={filter} apply={apply} />

                <div className="qf-group">
                    <ExtraFiltersButton
                        fields={REPORT_BILLING_EXTRA_FIELDS}
                        filter={filter}
                        apply={apply}
                        activeCount={extraCount}
                    />
                </div>

                <QuickResetLink onClick={() => replace(buildDefaultReportBillingFilter())} />

                {/* Ara satırı yok: etkin "diğer filtreler" etiketleri ayrı satır açmasın diye çubuğun sonunda. */}
                <ExtraFilterTags fields={REPORT_BILLING_EXTRA_FIELDS} filter={filter} apply={apply} />
            </div>
        </div>
    );
}
